import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  ContentCopy as ContentCopyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Public as PublicIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  Info as InfoIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import * as aiApi from '../../services/aiApi';
import * as mediaApi from '../../services/mediaApi';
import * as teamApi from '../../services/teamApi';
import * as integrationApi from '../../services/integrationApi';
import { getChannels, ChannelSummary } from '../../services/distributionApi';

// Advanced Features Components

// AI Copilot Component
export const AICopilot: React.FC = () => {
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const [userFeedback, setUserFeedback] = useState<{ [key: number]: 'like' | 'dislike' | null }>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prContent, setPrContent] = useState('');
  const [realData, setRealData] = useState<any>(null);

  const [suggestions, setSuggestions] = useState([
    {
      type: 'headline',
      title: 'Smart Headline Generation',
      content: [
        'Loading AI-generated headlines...'
      ],
      description: 'AI-generated headlines optimized for engagement and SEO'
    },
    {
      type: 'timing',
      title: 'Optimal Send Time Recommendations',
      content: [
        'Loading optimal timing recommendations...'
      ],
      description: 'Data-driven recommendations for maximum impact'
    },
    {
      type: 'enhancement',
      title: 'Content Enhancement Suggestions',
      content: [
        'Loading content suggestions...'
      ],
      description: 'AI-powered content optimization recommendations'
    },
    {
      type: 'channels',
      title: 'Channel Recommendations',
      content: [
        'Loading channel recommendations...'
      ],
      description: 'Intelligent channel matching based on content analysis'
    },
    {
      type: 'crisis',
      title: 'Crisis Detection & Monitoring',
      content: [
        'Loading crisis monitoring data...'
      ],
      description: 'Real-time crisis detection and sentiment monitoring'
    }
  ]);

  // Load user data and PR content
  useEffect(() => {
    const loadUserData = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setRealData(user);
          // Generate initial suggestions
          fetchAllSuggestions(user);
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };

    loadUserData();
  }, []);

  const fetchAllSuggestions = async (userData: any) => {
    setIsGenerating(true);
    setError(null);

    try {
      const user = userData || realData;
      const sampleContent = prContent || 'Sample press release content for AI analysis';

      // Fetch all suggestions in parallel
      const [
        headlinesData,
        timingData,
        contentSuggestionsData,
        channelData,
        crisisData
      ] = await Promise.allSettled([
        aiApi.generateHeadlines({ content: sampleContent, count: 4 }),
        aiApi.getOptimalTiming({ industry: user?.industry || 'Technology' }),
        aiApi.getContentSuggestions(sampleContent),
        aiApi.getChannelRecommendations({
          content: sampleContent,
          industry: user?.industry || 'Technology'
        }),
        aiApi.detectCrisis({ companyName: user?.company || 'Your Company' })
      ]);

      // Update suggestions with real data
      const newSuggestions = [...suggestions];

      // Headlines
      if (headlinesData.status === 'fulfilled') {
        newSuggestions[0].content = headlinesData.value.headlines || ['No headlines generated'];
      }

      // Timing
      if (timingData.status === 'fulfilled') {
        newSuggestions[1].content = timingData.value.recommendations.map(
          (rec: any) => `${rec.dateTime} - ${rec.reason}`
        );
      }

      // Enhancement suggestions
      if (contentSuggestionsData.status === 'fulfilled') {
        newSuggestions[2].content = contentSuggestionsData.value || ['No suggestions available'];
      }

      // Channels
      if (channelData.status === 'fulfilled') {
        newSuggestions[3].content = channelData.value.recommendations.map(
          (rec: any) => `${rec.channel} - ${rec.reason} (${rec.relevance}% relevance)`
        );
      }

      // Crisis detection
      if (crisisData.status === 'fulfilled') {
        const crisis = crisisData.value;
        newSuggestions[4].content = [
          `Sentiment monitoring: ${crisis.sentiment}`,
          `Risk Level: ${crisis.riskLevel}`,
          `Social media mentions: ${crisis.mentions}`,
          ...crisis.alerts.map((alert: any) => `${alert.severity}: ${alert.message}`)
        ];
      }

      setSuggestions(newSuggestions);
    } catch (error: any) {
      console.error('Error fetching suggestions:', error);
      setError('Failed to load AI suggestions. Using sample data.');
      // Fallback to sample data
      setSuggestions([
        {
          type: 'headline',
          title: 'Smart Headline Generation',
          content: [
            'Revolutionary AI Breakthrough Transforms Industry Landscape',
            'Breaking: Major Innovation Unveiled Today',
            'Industry-Leading Company Announces Game-Changing Solution',
            'Revolutionary Technology Sets New Industry Standards'
          ],
          description: 'AI-generated headlines optimized for engagement and SEO'
        },
        {
          type: 'timing',
          title: 'Optimal Send Time Recommendations',
          content: [
            'Tuesday 10:00 AM EST - Peak editorial hours',
            'Wednesday 2:00 PM EST - High engagement window',
            'Thursday 9:00 AM EST - Optimal for tech industry'
          ],
          description: 'Data-driven recommendations for maximum impact'
        },
        {
          type: 'enhancement',
          title: 'Content Enhancement Suggestions',
          content: [
            'Add compelling statistics to support your claims',
            'Include a clear call-to-action in the conclusion',
            'Consider adding multimedia elements for better engagement',
            'Optimize headline for better SEO performance'
          ],
          description: 'AI-powered content optimization recommendations'
        },
        {
          type: 'channels',
          title: 'Channel Recommendations',
          content: [
            'TechCrunch - High tech audience match (95% relevance)',
            'Forbes - Business and innovation focus (88% relevance)',
            'Reuters - Global reach and credibility (92% relevance)'
          ],
          description: 'Intelligent channel matching based on content analysis'
        },
        {
          type: 'crisis',
          title: 'Crisis Detection & Monitoring',
          content: [
            'Sentiment monitoring active - No issues detected',
            'Misinformation risk: Low (5/100)',
            'Brand reputation: Positive trend',
            'Social media mentions: Stable'
          ],
          description: 'Real-time crisis detection and sentiment monitoring'
        }
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFeedback = (index: number, feedback: 'like' | 'dislike') => {
    setUserFeedback(prev => ({ ...prev, [index]: feedback }));
  };

  const generateNewSuggestions = () => {
    fetchAllSuggestions(realData);
    setCurrentSuggestion(0);
  };

  const currentSuggestionData = suggestions[currentSuggestion];

  return (
    <Card style={{ marginBottom: "24px" }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <SmartToyIcon color="primary" style={{ marginRight: '16px', fontSize: 32 }} />
          <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Logo size="inline" animated={false} />AI Copilot
          </Typography>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert severity="warning" style={{ marginBottom: '16px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Suggestion Navigation */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {suggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion.title}
                variant={currentSuggestion === index ? 'filled' : 'outlined'}
                onClick={() => setCurrentSuggestion(index)}
                color={currentSuggestion === index ? 'primary' : 'default'}
              />
            ))}
          </div>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={generateNewSuggestions}
            disabled={isGenerating}
            style={{ marginBottom: "16px" }}
          >
            {isGenerating ? 'Generating...' : 'Generate New Suggestions'}
          </Button>
          
          {isGenerating && <LinearProgress style={{ 
            marginBottom: "16px"
          }} />}
        </div>

        {/* Current Suggestion */}
        <Paper style={{ padding: '24px', marginBottom: '24px' }}>
          <Typography variant="h6" gutterBottom>
            {currentSuggestionData.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {currentSuggestionData.description}
          </Typography>
          
          <List>
            {currentSuggestionData.content.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <AutoAwesomeIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Tooltip title="Like">
                    <IconButton
                      size="small"
                      color={userFeedback[index] === 'like' ? 'success' : 'default'}
                      onClick={() => handleFeedback(index, 'like')}
                    >
                      <ThumbUpIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Dislike">
                    <IconButton
                      size="small"
                      color={userFeedback[index] === 'dislike' ? 'error' : 'default'}
                      onClick={() => handleFeedback(index, 'dislike')}
                    >
                      <ThumbDownIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy">
                    <IconButton size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            startIcon={<SendIcon />}
            style={{
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
          >
            Apply Suggestions
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AssessmentIcon />}
            style={{
              borderColor: '#F36409',
              color: '#F36409',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            Analyze Performance
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<TimelineIcon />}
            style={{
              borderColor: '#F36409',
              color: '#F36409',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            View Trends
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Historical Success Rates Component
export const HistoricalSuccessRates: React.FC = () => {
  const [successData, setSuccessData] = useState<Array<{
    period: string;
    totalDistributions: number;
    accepted: number;
    published: number;
    rejected: number;
    pending: number;
    successRate: number;
    acceptanceRate: number;
    publicationRate: number;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y' | 'all'>('30d');

  useEffect(() => {
    loadSuccessRates();
  }, [timeRange]);

  const loadSuccessRates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real distribution data from API
      const axios = (await import('axios')).default;
      const { getToken } = await import('../../utils/tokenHelper');
      const { getApiBaseUrl } = await import('../../utils/apiConfig');
      
      // Align base URL construction with other API clients (like distributionApi)
      const RAW_API_BASE = getApiBaseUrl();
      const API_BASE_URL = `${RAW_API_BASE.replace(/\/$/, '')}/api`;
      const token = getToken();
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Get distributions directly from the API
      const distributionsResponse = await axios.get(`${API_BASE_URL}/distribution`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const distributions = distributionsResponse.data?.distributions || [];
      
      if (distributions.length === 0) {
        setSuccessData([]);
        setLoading(false);
        return;
      }
      
      // Calculate success rates by period
      const now = new Date();
      const timeRanges: { [key: string]: number } = {
        '7d': 7,
        '30d': 30,
        '90d': 90,
        '1y': 365,
        'all': Infinity
      };
      
      const daysBack = timeRanges[timeRange];
      const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
      
      // Filter distributions by date range
      const filtered = distributions.filter((dist: any) => {
        const distDate = new Date(dist.createdAt || dist.created_at || dist.created);
        return distDate >= cutoffDate;
      });
      
      // If no distributions found, show empty state
      if (filtered.length === 0) {
        setSuccessData([]);
        setLoading(false);
        return;
      }
      
      // Group by month/week
      const grouped: { [key: string]: any[] } = {};
      filtered.forEach((dist: any) => {
        const date = new Date(dist.createdAt || dist.created_at);
        let key: string;
        
        if (timeRange === '7d') {
          key = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        } else if (timeRange === '30d') {
          key = `Week ${Math.ceil(date.getDate() / 7)} - ${date.toLocaleDateString('en-US', { month: 'short' })}`;
        } else {
          key = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }
        
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(dist);
      });
      
      // Calculate metrics for each period
      const calculated = Object.entries(grouped).map(([period, dists]) => {
        const total = dists.length;
        const accepted = dists.filter((d: any) => d.status === 'accepted' || d.status === 'published').length;
        const published = dists.filter((d: any) => d.status === 'published').length;
        const rejected = dists.filter((d: any) => d.status === 'rejected' || d.status === 'denied').length;
        const pending = dists.filter((d: any) => d.status === 'pending' || d.status === 'sent').length;
        
        return {
          period,
          totalDistributions: total,
          accepted,
          published,
          rejected,
          pending,
          successRate: total > 0 ? Math.round((published / total) * 100) : 0,
          acceptanceRate: total > 0 ? Math.round((accepted / total) * 100) : 0,
          publicationRate: accepted > 0 ? Math.round((published / accepted) * 100) : 0,
        };
      }).sort((a, b) => {
        // Sort by date (most recent first)
        return new Date(b.period).getTime() - new Date(a.period).getTime();
      });
      
      setSuccessData(calculated);
    } catch (err: any) {
      console.error('Error loading success rates:', err);
      setError(err.message || 'Failed to load success rates');
      setSuccessData([]);
    } finally {
      setLoading(false);
    }
  };

  const getSuccessColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const overallStats = successData.length > 0 ? {
    total: successData.reduce((sum, d) => sum + d.totalDistributions, 0),
    published: successData.reduce((sum, d) => sum + d.published, 0),
    accepted: successData.reduce((sum, d) => sum + d.accepted, 0),
    rejected: successData.reduce((sum, d) => sum + d.rejected, 0),
    avgSuccessRate: Math.round(
      successData.reduce((sum, d) => sum + d.successRate, 0) / successData.length
    ),
    avgAcceptanceRate: Math.round(
      successData.reduce((sum, d) => sum + d.acceptanceRate, 0) / successData.length
    ),
  } : null;

  return (
    <Card style={{ marginBottom: "24px" }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Typography variant="h6" gutterBottom>
            Historical Success Rates
          </Typography>
          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              label="Time Range"
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </div>

        {error && (
          <Alert severity="warning" style={{ marginBottom: "24px" }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && (
          <LinearProgress style={{ marginBottom: "16px" }} />
        )}

        {/* Overall Stats */}
        {overallStats && (
          <Grid container spacing={2} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper style={{ padding: "16px", textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Distributions
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                  {overallStats.total}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper style={{ padding: "16px", textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Published
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: '#4caf50' }}>
                  {overallStats.published}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper style={{ padding: "16px", textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Avg Success Rate
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: '#4caf50' }}>
                  {overallStats.avgSuccessRate}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper style={{ padding: "16px", textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Avg Acceptance Rate
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: '#ff9800' }}>
                  {overallStats.avgAcceptanceRate}%
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Historical Data Table */}
        <Paper style={{ padding: "16px" }}>
          <Typography variant="subtitle1" gutterBottom>
            Success Rate by Period
          </Typography>
          
          {successData.length === 0 && !loading ? (
            <Alert severity="info" style={{ marginTop: '16px' }}>
              No distribution data available for the selected time range.
            </Alert>
          ) : (
            <List>
              {successData.map((data, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUpIcon color={getSuccessColor(data.successRate) as any} />
                    </ListItemIcon>
                    <ListItemText
                      primary={data.period}
                      secondary={
                        <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                          <Chip 
                            label={`${data.totalDistributions} Total`} 
                            size="small" 
                            variant="outlined"
                          />
                          <Chip 
                            label={`${data.published} Published`} 
                            size="small" 
                            color="success"
                          />
                          <Chip 
                            label={`${data.accepted} Accepted`} 
                            size="small" 
                            color="warning"
                          />
                          <Chip 
                            label={`${data.rejected} Rejected`} 
                            size="small" 
                            color="error"
                          />
                          {data.pending > 0 && (
                            <Chip 
                              label={`${data.pending} Pending`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </div>
                      }
                    />
                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                      <Chip
                        label={`${data.successRate}% Success`}
                        color={getSuccessColor(data.successRate) as any}
                        style={{ marginBottom: '4px' }}
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        {data.acceptanceRate}% Acceptance
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {data.publicationRate}% Publication
                      </Typography>
                    </div>
                  </ListItem>
                  {index < successData.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </CardContent>
    </Card>
  );
};

// Multimedia Management Component
export const MultimediaManagement: React.FC = () => {
  const [mediaLibrary, setMediaLibrary] = useState<mediaApi.MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newMedia, setNewMedia] = useState({
    name: '',
    type: 'image' as 'image' | 'video' | 'document' | 'audio',
    usage: '',
    rights: 'Full usage rights' as 'Full usage rights' | 'Limited usage rights' | 'Editorial use only',
    description: ''
  });

  useEffect(() => {
    loadMediaAssets();
  }, []);

  const loadMediaAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const assets = await mediaApi.getMediaAssets();
      setMediaLibrary(assets);
    } catch (err: any) {
      console.error('Error loading media assets:', err);
      setError(err.message || 'Failed to load media assets');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !newMedia.name) {
      setError('Please select a file and enter a name');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await mediaApi.uploadMediaAsset(selectedFile, {
        name: newMedia.name,
        type: newMedia.type,
        usage: newMedia.usage || 'Press releases, social media',
        rights: newMedia.rights,
        description: newMedia.description
      });
      await loadMediaAssets();
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setNewMedia({ name: '', type: 'image', usage: '', rights: 'Full usage rights', description: '' });
    } catch (err: any) {
      console.error('Error uploading media:', err);
      setError(err.message || 'Failed to upload media asset');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this media asset?')) return;

    try {
      await mediaApi.deleteMediaAsset(id);
      await loadMediaAssets();
    } catch (err: any) {
      console.error('Error deleting media:', err);
      setError(err.message || 'Failed to delete media asset');
    }
  };

  const handleDownload = async (id: string) => {
    try {
      await mediaApi.downloadMediaAsset(id);
      await loadMediaAssets();
    } catch (err: any) {
      console.error('Error downloading media:', err);
      setError(err.message || 'Failed to download media asset');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return <ImageIcon />;
      case 'video': return <VideoLibraryIcon />;
      case 'document': return <DescriptionIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'image': return 'primary';
      case 'video': return 'secondary';
      case 'document': return 'success';
      default: return 'default';
    }
  };

  return (
    <Card style={{ marginBottom: "24px" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Multimedia Management
        </Typography>
        
        {error && (
          <Alert severity="error" style={{ marginBottom: '16px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Typography variant="body2" color="text.secondary">
            Digital asset library for images, videos, and documents
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Media
          </Button>
        </div>

        {loading && <LinearProgress style={{ marginBottom: '16px' }} />}

        {mediaLibrary.length === 0 && !loading ? (
          <Alert severity="info">No media assets found. Upload your first asset to get started.</Alert>
        ) : (
          <Grid container spacing={2}>
            {mediaLibrary.map((media) => (
              <Grid item xs={12} md={6} lg={4} key={media._id}>
                <Paper style={{ padding: "16px" }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <Avatar style={{ backgroundColor: getTypeColor(media.type), marginRight: '16px' }}>
                      {getTypeIcon(media.type)}
                    </Avatar>
                    <div style={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2">{media.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {media.type.toUpperCase()} • {media.formattedSize || `${(media.size / (1024 * 1024)).toFixed(1)} MB`}
                      </Typography>
                    </div>
                  </div>
                  
                  <Typography variant="body2" gutterBottom>
                    Uploaded: {new Date(media.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Usage: {media.usage}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Rights: {media.rights}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Downloads: {media.downloads}
                  </Typography>
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => handleDownload(media._id)}
                      disabled={loading}
                    >
                      Download
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => window.open(media.url, '_blank')}
                      title="Preview in new tab"
                    >
                      Preview
                    </Button>
                    <Button variant="outlined" size="small" color="error" onClick={() => handleDelete(media._id)}>
                      Delete
                    </Button>
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload New Media</DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '16px' }}>
              <input
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedFile(file);
                    if (!newMedia.name) {
                      setNewMedia(prev => ({ ...prev, name: file.name }));
                    }
                    // Auto-detect type
                    if (file.type.startsWith('image/')) {
                      setNewMedia(prev => ({ ...prev, type: 'image' }));
                    } else if (file.type.startsWith('video/')) {
                      setNewMedia(prev => ({ ...prev, type: 'video' }));
                    } else {
                      setNewMedia(prev => ({ ...prev, type: 'document' }));
                    }
                  }
                }}
                style={{ marginBottom: "16px" }}
              />
              
              <TextField
                fullWidth
                label="Media Name"
                value={newMedia.name}
                onChange={(e) => setNewMedia(prev => ({ ...prev, name: e.target.value }))}
                style={{ marginBottom: "16px" }}
                required
              />
              
              <FormControl fullWidth style={{ marginBottom: "16px" }}>
                <InputLabel>Media Type</InputLabel>
                <Select
                  value={newMedia.type}
                  onChange={(e) => setNewMedia(prev => ({ ...prev, type: e.target.value as any }))}
                  label="Media Type"
                >
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="document">Document</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Usage"
                value={newMedia.usage}
                onChange={(e) => setNewMedia(prev => ({ ...prev, usage: e.target.value }))}
                placeholder="e.g., Press releases, social media"
                style={{ marginBottom: "16px" }}
              />
              
              <TextField
                fullWidth
                label="Description"
                value={newMedia.description}
                onChange={(e) => setNewMedia(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
                style={{ marginBottom: "16px" }}
              />
              
              <FormControl fullWidth>
                <InputLabel>Rights</InputLabel>
                <Select
                  value={newMedia.rights}
                  onChange={(e) => setNewMedia(prev => ({ ...prev, rights: e.target.value as any }))}
                  label="Rights"
                >
                  <MenuItem value="Full usage rights">Full Usage Rights</MenuItem>
                  <MenuItem value="Limited usage rights">Limited Usage Rights</MenuItem>
                  <MenuItem value="Editorial use only">Editorial Use Only</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFile(null);
              setNewMedia({ name: '', type: 'image', usage: '', rights: 'Full usage rights', description: '' });
            }}>Cancel</Button>
            <Button variant="contained" onClick={handleUpload} disabled={uploading || !selectedFile || !newMedia.name}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Collaboration Tools Component
export const CollaborationTools: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<teamApi.TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'PR Manager' | 'Content Writer' | 'Marketing Director' | 'Editor' | 'Viewer'>('Viewer');
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [newStep, setNewStep] = useState({ role: '', action: '' });

  // Load workflow from localStorage or use default
  const loadWorkflow = () => {
    try {
      const saved = localStorage.getItem('approvalWorkflow');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
    }
    return {
      enabled: true,
      steps: [
        { role: 'Content Writer', action: 'Create Draft' },
        { role: 'PR Manager', action: 'Review & Edit' },
        { role: 'Marketing Director', action: 'Final Approval' },
        { role: 'System', action: 'Publish' }
      ]
    };
  };

  const [approvalWorkflow, setApprovalWorkflow] = useState(loadWorkflow());

  // Save workflow to localStorage
  const saveWorkflow = (workflow: typeof approvalWorkflow) => {
    try {
      localStorage.setItem('approvalWorkflow', JSON.stringify(workflow));
    } catch (error) {
      console.error('Error saving workflow:', error);
    }
  };

  useEffect(() => {
    loadTeamMembers();
    // Update last active on mount
    teamApi.updateLastActive();
  }, []);

  // Save workflow whenever it changes
  useEffect(() => {
    saveWorkflow(approvalWorkflow);
  }, [approvalWorkflow]);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const members = await teamApi.getTeamMembers();
      setTeamMembers(members);
    } catch (err: any) {
      console.error('Error loading team members:', err);
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!newMemberEmail) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setInviting(true);
      setError(null);
      await teamApi.addTeamMember({
        email: newMemberEmail.trim().toLowerCase(),
        role: newMemberRole,
        permissions: getDefaultPermissions(newMemberRole)
      });
      await loadTeamMembers();
      setSuccessMessage(`Invitation email sent to ${newMemberEmail}! They will receive an email to accept the invitation.`);
      setInviteDialogOpen(false);
      setNewMemberEmail('');
      setNewMemberRole('Viewer');
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error inviting team member:', err);
      setError(err.message || 'Failed to invite team member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (id: string) => {
    if (!id) {
      setError('Invalid team member ID');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      setError(null);
      await teamApi.removeTeamMember(id);
      await loadTeamMembers();
    } catch (err: any) {
      console.error('Error removing team member:', err);
      setError(err.message || 'Failed to remove team member');
    }
  };

  const getDefaultPermissions = (role: string): string[] => {
    switch (role) {
      case 'PR Manager': return ['create', 'edit', 'approve', 'view'];
      case 'Content Writer': return ['create', 'edit', 'view'];
      case 'Marketing Director': return ['create', 'edit', 'approve', 'publish', 'view'];
      case 'Editor': return ['create', 'edit', 'view'];
      case 'Viewer': return ['view'];
      default: return ['view'];
    }
  };

  const formatLastActive = (lastActive?: string) => {
    if (!lastActive) return 'Never';
    try {
      const date = new Date(lastActive);
      if (isNaN(date.getTime())) return 'Never';
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      if (diffMs < 0) return 'Just now';
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'Never';
    }
  };

  const getAvatarInitials = (member: teamApi.TeamMember): string => {
    try {
      if (member.memberId?.fullName) {
        const initials = member.memberId.fullName
          .trim()
          .split(' ')
          .filter(n => n.length > 0)
          .map(n => n[0])
          .join('')
          .toUpperCase();
        if (initials.length > 0) return initials.substring(0, 2);
      }
      if (member.memberId?.email) {
        return member.memberId.email[0].toUpperCase();
      }
      return 'U';
    } catch (error) {
      return 'U';
    }
  };

  const getMemberDisplayName = (member: teamApi.TeamMember): string => {
    if (member.memberId?.fullName) {
      return member.memberId.fullName;
    }
    if (member.memberId?.email) {
      return member.memberId.email;
    }
    return 'Unknown User';
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'approval': return <CheckCircleIcon color="success" />;
      case 'edit': return <EditIcon color="primary" />;
      case 'comment': return <CommentIcon color="info" />;
      default: return <InfoIcon />;
    }
  };

  const handleAddWorkflowStep = () => {
    if (!newStep.role || !newStep.action) {
      setError('Please enter both role and action');
      return;
    }
    setApprovalWorkflow((prev: typeof approvalWorkflow) => ({
      ...prev,
      steps: [...prev.steps, { ...newStep }]
    }));
    setNewStep({ role: '', action: '' });
  };

  const handleRemoveWorkflowStep = (index: number) => {
    setApprovalWorkflow((prev: typeof approvalWorkflow) => ({
      ...prev,
      steps: prev.steps.filter((_: any, i: number) => i !== index)
    }));
  };

  const handleUpdateWorkflowStep = (index: number, field: 'role' | 'action', value: string) => {
    setApprovalWorkflow((prev: typeof approvalWorkflow) => ({
      ...prev,
      steps: prev.steps.map((step: any, i: number) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === approvalWorkflow.steps.length - 1) return;

    const newSteps = [...approvalWorkflow.steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    setApprovalWorkflow((prev: typeof approvalWorkflow) => ({
      ...prev,
      steps: newSteps
    }));
  };

  return (
    <Card style={{ marginBottom: "24px" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Collaboration Tools
        </Typography>
        
        {error && (
          <Alert severity="error" style={{ marginBottom: '16px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" style={{ marginBottom: '16px' }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {loading && <LinearProgress style={{ marginBottom: '16px' }} />}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper style={{ padding: "16px" }}>
              <Typography variant="subtitle1" gutterBottom>
                Team Workspace
              </Typography>
              
              {teamMembers.length === 0 && !loading ? (
                <Alert severity="info" style={{ marginTop: '16px' }}>
                  No team members yet. Invite your first team member to get started.
                </Alert>
              ) : (
                <List>
                  {teamMembers.map((member) => {
                    const isPending = member.status === 'pending';
                    const displayEmail = member.memberId?.email || member.email;
                    const displayName = member.memberId?.fullName || displayEmail;
                    
                    return (
                      <ListItem key={member._id} style={{ 
                        opacity: isPending ? 0.7 : 1,
                        backgroundColor: isPending ? '#fff3cd' : 'transparent',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}>
                        <Avatar style={{ marginRight: '16px', backgroundColor: isPending ? '#ff9800' : '#0927eb' }}>
                          {isPending ? <ScheduleIcon /> : getAvatarInitials(member)}
                        </Avatar>
                        <ListItemText
                          primary={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{displayName}</span>
                              {isPending && (
                                <Chip 
                                  label="Pending" 
                                  size="small" 
                                  color="warning"
                                  style={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              )}
                              {member.status === 'accepted' && (
                                <Chip 
                                  label="Active" 
                                  size="small" 
                                  color="success"
                                  style={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              )}
                            </div>
                          }
                          secondary={
                            isPending 
                              ? `${member.role || 'Viewer'} • Invitation sent to ${displayEmail} • ${member.invitationExpires ? `Expires: ${new Date(member.invitationExpires).toLocaleDateString()}` : 'No expiry date'}`
                              : `${member.role || 'Viewer'} • Last active: ${formatLastActive(member.lastActive)}`
                          }
                        />
                        <div style={{ textAlign: 'right' }}>
                          {!isPending && (
                            <Typography variant="body2" color="text.secondary">
                              {(member.permissions && Array.isArray(member.permissions) ? member.permissions : ['view']).join(', ')}
                            </Typography>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveMember(member._id)}
                            aria-label={isPending ? "Cancel invitation" : "Remove team member"}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                fullWidth 
                style={{ marginTop: "16px" }}
                onClick={() => setInviteDialogOpen(true)}
              >
                Invite Team Member
              </Button>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper style={{ padding: "16px" }}>
              <Typography variant="subtitle1" gutterBottom>
                Approval Workflow
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={approvalWorkflow.enabled}
                    onChange={(e) => setApprovalWorkflow((prev: typeof approvalWorkflow) => ({ ...prev, enabled: e.target.checked }))}
                  />
                }
                label="Enable Approval Workflow"
                style={{ marginBottom: "16px" }}
              />
              
              {approvalWorkflow.enabled && (
                <List>
                  {approvalWorkflow.steps.map((step: { role: string; action: string }, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar style={{ backgroundColor: '#0927eb', width: 24, height: 24 }}>
                          {index + 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={step.action}
                        secondary={step.role}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                fullWidth 
                style={{ marginTop: "16px" }}
                onClick={() => setWorkflowDialogOpen(true)}
              >
                Configure Workflow
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Invite Dialog */}
        <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '16px' }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="user@example.com"
                style={{ marginBottom: "16px" }}
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value as any)}
                  label="Role"
                >
                  <MenuItem value="Viewer">Viewer</MenuItem>
                  <MenuItem value="Content Writer">Content Writer</MenuItem>
                  <MenuItem value="Editor">Editor</MenuItem>
                  <MenuItem value="PR Manager">PR Manager</MenuItem>
                  <MenuItem value="Marketing Director">Marketing Director</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setInviteDialogOpen(false);
              setNewMemberEmail('');
              setNewMemberRole('Viewer');
            }}>Cancel</Button>
            <Button variant="contained" onClick={handleInviteMember} disabled={inviting || !newMemberEmail}>
              {inviting ? 'Inviting...' : 'Invite'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Workflow Configuration Dialog */}
        <Dialog open={workflowDialogOpen} onClose={() => setWorkflowDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Configure Approval Workflow</DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '16px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={approvalWorkflow.enabled}
                    onChange={(e) => setApprovalWorkflow((prev: typeof approvalWorkflow) => ({ ...prev, enabled: e.target.checked }))}
                  />
                }
                label="Enable Approval Workflow"
                style={{ marginBottom: "16px" }}
              />

              <Typography variant="subtitle2" gutterBottom style={{ marginTop: '16px', marginBottom: '8px' }}>
                Workflow Steps
              </Typography>

              <List>
                {approvalWorkflow.steps.map((step: { role: string; action: string }, index: number) => (
                  <ListItem key={index} style={{ border: '1px solid #e0e0e0', marginBottom: '8px', borderRadius: '4px' }}>
                    <ListItemIcon>
                      <Avatar style={{ backgroundColor: '#0927eb', width: 32, height: 32 }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        editingStepIndex === index ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <FormControl fullWidth size="small" style={{ marginBottom: '8px' }}>
                              <InputLabel>Role</InputLabel>
                              <Select
                                value={step.role}
                                onChange={(e) => handleUpdateWorkflowStep(index, 'role', e.target.value)}
                                label="Role"
                              >
                                <MenuItem value="Content Writer">Content Writer</MenuItem>
                                <MenuItem value="PR Manager">PR Manager</MenuItem>
                                <MenuItem value="Marketing Director">Marketing Director</MenuItem>
                                <MenuItem value="Editor">Editor</MenuItem>
                                <MenuItem value="Viewer">Viewer</MenuItem>
                                <MenuItem value="System">System</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              fullWidth
                              size="small"
                              label="Action"
                              value={step.action}
                              onChange={(e) => handleUpdateWorkflowStep(index, 'action', e.target.value)}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => setEditingStepIndex(null)}
                              style={{ marginTop: '8px' }}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                              {step.action}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {step.role}
                            </Typography>
                          </div>
                        )
                      }
                    />
                    {editingStepIndex !== index && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveStep(index, 'up')}
                          disabled={index === 0}
                          aria-label="Move up"
                        >
                          <TrendingUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveStep(index, 'down')}
                          disabled={index === approvalWorkflow.steps.length - 1}
                          aria-label="Move down"
                        >
                          <TrendingUpIcon fontSize="small" style={{ transform: 'rotate(180deg)' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setEditingStepIndex(index)}
                          aria-label="Edit step"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveWorkflowStep(index)}
                          color="error"
                          aria-label="Remove step"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                  </ListItem>
                ))}
              </List>

              <Divider style={{ margin: '16px 0' }} />

              <Typography variant="subtitle2" gutterBottom style={{ marginBottom: '8px' }}>
                Add New Step
              </Typography>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <FormControl style={{ flex: 1 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newStep.role}
                    onChange={(e) => setNewStep(prev => ({ ...prev, role: e.target.value }))}
                    label="Role"
                  >
                    <MenuItem value="Content Writer">Content Writer</MenuItem>
                    <MenuItem value="PR Manager">PR Manager</MenuItem>
                    <MenuItem value="Marketing Director">Marketing Director</MenuItem>
                    <MenuItem value="Editor">Editor</MenuItem>
                    <MenuItem value="Viewer">Viewer</MenuItem>
                    <MenuItem value="System">System</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  style={{ flex: 1 }}
                  label="Action"
                  value={newStep.action}
                  onChange={(e) => setNewStep(prev => ({ ...prev, action: e.target.value }))}
                  placeholder="e.g., Review & Approve"
                />
                <Button
                  variant="contained"
                  onClick={handleAddWorkflowStep}
                  disabled={!newStep.role || !newStep.action}
                  startIcon={<CheckCircleIcon />}
                >
                  Add Step
                </Button>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setWorkflowDialogOpen(false);
              setEditingStepIndex(null);
              setNewStep({ role: '', action: '' });
            }}>
              Close
            </Button>
            <Button 
              variant="contained" 
              onClick={() => {
                setWorkflowDialogOpen(false);
                setEditingStepIndex(null);
                setNewStep({ role: '', action: '' });
                setSuccessMessage('Workflow configuration saved successfully!');
                setTimeout(() => setSuccessMessage(null), 5000);
              }}
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Integration Ecosystem Component
export const IntegrationEcosystem: React.FC = () => {
  const [integrations, setIntegrations] = useState<integrationApi.Integration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<{ name: string; category: 'CRM' | 'Marketing' | 'Analytics' | 'Communication' | 'Social Media' | 'Other'; description: string } | null>(null);
  const [connectionData, setConnectionData] = useState({
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    description: ''
  });
  const [updateCredentialsDialogOpen, setUpdateCredentialsDialogOpen] = useState(false);
  const [updatingCredentials, setUpdatingCredentials] = useState(false);
  const [selectedIntegrationForUpdate, setSelectedIntegrationForUpdate] = useState<integrationApi.Integration | null>(null);
  const [updateCredentialsData, setUpdateCredentialsData] = useState({
    accessToken: '',
    apiKey: '',
    apiSecret: ''
  });
  const [integrationMetrics, setIntegrationMetrics] = useState<{ [key: string]: { latestMetrics: any; history: any[] } }>({});
  const [syncingIntegrations, setSyncingIntegrations] = useState<Set<string>>(new Set());

  const availableIntegrations = [
    { name: 'Google Analytics', category: 'Analytics' as const, description: 'Track website traffic from PR campaigns' },
    { name: 'Slack', category: 'Communication' as const, description: 'Get real-time notifications for PR activities' },
    { name: 'Mailchimp', category: 'Marketing' as const, description: 'Email marketing integration' }
  ];

  useEffect(() => {
    loadIntegrations();
    
    // Check for OAuth callback success/error
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    
    if (success === 'hubspot_connected') {
      setSuccessMessage('HubSpot connected successfully!');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Reload integrations
      loadIntegrations();
      setTimeout(() => setSuccessMessage(null), 5000);
    }
    
    if (error) {
      setError(decodeURIComponent(error));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setError(null), 10000);
    }
  }, []);

  // Load metrics for connected integrations
  useEffect(() => {
    const loadMetrics = async () => {
      for (const integration of integrations) {
        if (integration.status === 'connected' && integration.lastSync) {
          try {
            const metrics = await integrationApi.getIntegrationMetrics(integration._id);
            setIntegrationMetrics(prev => ({
              ...prev,
              [integration._id]: metrics
            }));
          } catch (err) {
            // Silently fail - metrics are optional
            console.warn(`Failed to load metrics for ${integration.name}:`, err);
          }
        }
      }
    };

    if (integrations.length > 0) {
      loadMetrics();
    }
  }, [integrations]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await integrationApi.getIntegrations();
      setIntegrations(data);
    } catch (err: any) {
      console.error('Error loading integrations:', err);
      setError(err.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedIntegration) return;

    // Handle HubSpot OAuth flow
    if (selectedIntegration.name === 'HubSpot') {
      try {
        setConnecting(true);
        setError(null);
        const { authUrl } = await integrationApi.getHubspotOAuthUrl();
        // Redirect to HubSpot OAuth
        window.location.href = authUrl;
      } catch (err: any) {
        console.error('Error getting HubSpot OAuth URL:', err);
        setError(err.message || 'Failed to initiate OAuth flow');
        setConnecting(false);
      }
      return;
    }

    // For other integrations, use manual credentials
    try {
      setConnecting(true);
      setError(null);
      await integrationApi.connectIntegration({
        name: selectedIntegration.name,
        category: selectedIntegration.category,
        apiKey: connectionData.apiKey || undefined,
        apiSecret: connectionData.apiSecret || undefined,
        accessToken: connectionData.accessToken || undefined,
        description: connectionData.description || selectedIntegration.description
      });
      await loadIntegrations();
      setConnectDialogOpen(false);
      setSelectedIntegration(null);
      setConnectionData({ apiKey: '', apiSecret: '', accessToken: '', description: '' });
    } catch (err: any) {
      console.error('Error connecting integration:', err);
      setError(err.message || 'Failed to connect integration');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (id: string) => {
    if (!window.confirm('Are you sure you want to disconnect this integration?')) return;

    try {
      await integrationApi.disconnectIntegration(id);
      await loadIntegrations();
    } catch (err: any) {
      console.error('Error disconnecting integration:', err);
      setError(err.message || 'Failed to disconnect integration');
    }
  };

  const handleUpdateConfig = async (id: string, propertyId: string) => {
    try {
      await integrationApi.updateIntegration(id, {
        config: {
          propertyId: propertyId
        }
      });
      await loadIntegrations();
      setError(null);
    } catch (err: any) {
      console.error('Error updating integration config:', err);
      setError(err.message || 'Failed to update integration config');
    }
  };

  const handleUpdateCredentials = async () => {
    if (!selectedIntegrationForUpdate) return;

    try {
      setUpdatingCredentials(true);
      setError(null);
      
      const updateData: any = {};
      if (updateCredentialsData.accessToken) updateData.accessToken = updateCredentialsData.accessToken;
      if (updateCredentialsData.apiKey) updateData.apiKey = updateCredentialsData.apiKey;
      if (updateCredentialsData.apiSecret) updateData.apiSecret = updateCredentialsData.apiSecret;

      await integrationApi.updateIntegration(selectedIntegrationForUpdate._id, updateData);
      await loadIntegrations();
      setUpdateCredentialsDialogOpen(false);
      setSelectedIntegrationForUpdate(null);
      setUpdateCredentialsData({ accessToken: '', apiKey: '', apiSecret: '' });
      
      // Show success message
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error updating credentials:', err);
      setError(err.message || 'Failed to update credentials');
    } finally {
      setUpdatingCredentials(false);
    }
  };

  const handleSync = async (id: string) => {
    try {
      setSyncingIntegrations(prev => new Set(prev).add(id));
      setError(null);
      
      const result = await integrationApi.syncIntegration(id);
      
      // Load updated integrations
      await loadIntegrations();
      
      // Fetch and display metrics
      if (result.metrics) {
        const metrics = await integrationApi.getIntegrationMetrics(id);
        setIntegrationMetrics(prev => ({
          ...prev,
          [id]: metrics
        }));
      }
      
      // Show success message
      if (result.metrics) {
        console.log('✅ Integration synced successfully with metrics:', result.metrics);
      }
    } catch (err: any) {
      console.error('Error syncing integration:', err);
      setError(err.message || 'Failed to sync integration');
    } finally {
      setSyncingIntegrations(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      await integrationApi.deleteIntegration(id);
      await loadIntegrations();
    } catch (err: any) {
      console.error('Error deleting integration:', err);
      setError(err.message || 'Failed to delete integration');
    }
  };

  const formatLastSync = (lastSync?: string) => {
    if (!lastSync) return 'Never';
    const date = new Date(lastSync);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    return status === 'connected' ? 'success' : 'error';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'CRM': return <BusinessIcon />;
      case 'Marketing': return <TrendingUpIcon />;
      case 'Analytics': return <AssessmentIcon />;
      case 'Communication': return <GroupIcon />;
      case 'Social Media': return <PublicIcon />;
      default: return <LinkIcon />;
    }
  };

  return (
    <Card style={{ marginBottom: "24px" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Integration Ecosystem
        </Typography>
        
        {error && (
          <Alert severity="error" style={{ marginBottom: '16px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" style={{ marginBottom: '16px' }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {loading && <LinearProgress style={{ marginBottom: '16px' }} />}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper style={{ padding: "16px" }}>
              <Typography variant="subtitle1" gutterBottom>
                Connected Integrations
              </Typography>
              
              {integrations.length === 0 && !loading ? (
                <Alert severity="info" style={{ marginTop: '16px' }}>
                  No integrations connected yet. Connect an integration to get started.
                </Alert>
              ) : (
                <List>
                  {integrations.map((integration) => {
                    const metrics = integrationMetrics[integration._id]?.latestMetrics || {};
                    const isSyncing = syncingIntegrations.has(integration._id);
                    
                    return (
                      <ListItem key={integration._id}>
                        <ListItemIcon>
                          <Avatar style={{ backgroundColor: '#0927eb' }}>
                            {getCategoryIcon(integration.category)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <div>
                              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                {integration.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: '4px' }}>
                                {integration.description || ''} • Last sync: {formatLastSync(integration.lastSync)}
                              </Typography>
                              {/* Display real metrics */}
                              {Object.keys(metrics).length > 0 && (
                                <Box style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {integration.name === 'Google Analytics' && (
                                    <>
                                      {metrics.sessions !== undefined && (
                                        <Chip label={`${metrics.sessions} Sessions`} size="small" color="primary" />
                                      )}
                                      {metrics.users !== undefined && (
                                        <Chip label={`${metrics.users} Users`} size="small" color="primary" />
                                      )}
                                      {metrics.pageViews !== undefined && (
                                        <Chip label={`${metrics.pageViews} Views`} size="small" color="primary" />
                                      )}
                                      {metrics.activeUsers !== undefined && (
                                        <Chip label={`${metrics.activeUsers} Active`} size="small" color="success" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Slack' && (
                                    <>
                                      {metrics.totalChannels !== undefined && (
                                        <Chip label={`${metrics.totalChannels} Channels`} size="small" color="primary" />
                                      )}
                                      {metrics.recentActivity !== undefined && (
                                        <Chip label={`${metrics.recentActivity} Messages`} size="small" color="primary" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Mailchimp' && (
                                    <>
                                      {metrics.totalLists !== undefined && (
                                        <Chip label={`${metrics.totalLists} Lists`} size="small" color="primary" />
                                      )}
                                      {metrics.totalSubscribers !== undefined && (
                                        <Chip label={`${metrics.totalSubscribers} Subscribers`} size="small" color="success" />
                                      )}
                                      {metrics.totalCampaigns !== undefined && (
                                        <Chip label={`${metrics.totalCampaigns} Campaigns`} size="small" color="primary" />
                                      )}
                                      {metrics.accountName && (
                                        <Chip label={metrics.accountName} size="small" color="default" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'HubSpot' && (
                                    <>
                                      {metrics.totalContacts !== undefined && (
                                        <Chip label={`${metrics.totalContacts} Contacts`} size="small" color="primary" />
                                      )}
                                      {metrics.prGeneratedLeads !== undefined && (
                                        <Chip label={`${metrics.prGeneratedLeads} PR Leads`} size="small" color="success" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Salesforce CRM' && (
                                    <>
                                      {metrics.totalContacts !== undefined && (
                                        <Chip label={`${metrics.totalContacts} Contacts`} size="small" color="primary" />
                                      )}
                                      {metrics.prGeneratedLeads !== undefined && (
                                        <Chip label={`${metrics.prGeneratedLeads} PR Leads`} size="small" color="success" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Buffer' && (
                                    <>
                                      {metrics.totalProfiles !== undefined && (
                                        <Chip label={`${metrics.totalProfiles} Profiles`} size="small" color="primary" />
                                      )}
                                      {metrics.sentPosts !== undefined && (
                                        <Chip label={`${metrics.sentPosts} Posts`} size="small" color="success" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Mailchimp' && (
                                    <>
                                      {metrics.totalSubscribers !== undefined && (
                                        <Chip label={`${metrics.totalSubscribers} Subscribers`} size="small" color="primary" />
                                      )}
                                      {metrics.totalCampaigns !== undefined && (
                                        <Chip label={`${metrics.totalCampaigns} Campaigns`} size="small" color="primary" />
                                      )}
                                    </>
                                  )}
                                  {integration.name === 'Microsoft Teams' && (
                                    <>
                                      {metrics.totalTeams !== undefined && (
                                        <Chip label={`${metrics.totalTeams} Teams`} size="small" color="primary" />
                                      )}
                                      {metrics.recentMessages !== undefined && (
                                        <Chip label={`${metrics.recentMessages} Messages`} size="small" color="primary" />
                                      )}
                                    </>
                                  )}
                                </Box>
                              )}
                              {integration.lastSyncStatus === 'failed' && integration.lastSyncError && (
                                <Box style={{ marginTop: '8px' }}>
                                  <Alert severity="error" style={{ fontSize: '0.75rem', padding: '8px', marginBottom: '8px' }}>
                                    {integration.lastSyncError}
                                  </Alert>
                                  {(integration.name === 'HubSpot' || integration.name === 'Buffer' || integration.name === 'Mailchimp' || integration.name === 'Salesforce CRM') && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => {
                                        setSelectedIntegrationForUpdate(integration);
                                        setUpdateCredentialsData({ accessToken: '', apiKey: '', apiSecret: '' });
                                        setUpdateCredentialsDialogOpen(true);
                                      }}
                                      style={{ marginTop: '4px' }}
                                    >
                                      Update Credentials
                                    </Button>
                                  )}
                                </Box>
                              )}
                              {/* Show Property ID input for Google Analytics if missing */}
                              {integration.name === 'Google Analytics' && !integration.config?.propertyId && (
                                <Box style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                                  <Typography variant="caption" style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                                    ⚠️ Property ID Required
                                  </Typography>
                                  <TextField
                                    size="small"
                                    placeholder="Enter GA4 Property ID (e.g., 123456789)"
                                    value={connectionData.apiKey}
                                    onChange={(e) => setConnectionData(prev => ({ ...prev, apiKey: e.target.value }))}
                                    style={{ width: '100%', marginBottom: '4px' }}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      if (connectionData.apiKey && /^\d+$/.test(connectionData.apiKey)) {
                                        handleUpdateConfig(integration._id, connectionData.apiKey);
                                        setConnectionData(prev => ({ ...prev, apiKey: '' }));
                                      } else {
                                        setError('Property ID must be numeric (e.g., 123456789)');
                                      }
                                    }}
                                    style={{ width: '100%' }}
                                  >
                                    Save Property ID
                                  </Button>
                                  <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: '4px' }}>
                                    Get your Property ID from Google Analytics → Admin → Property Settings
                                  </Typography>
                                </Box>
                              )}
                            </div>
                          }
                        />
                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Chip
                            label={integration.status}
                            color={getStatusColor(integration.status) as any}
                            size="small"
                          />
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {integration.status === 'connected' && (
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => handleSync(integration._id)}
                                disabled={isSyncing}
                              >
                                {isSyncing ? 'Syncing...' : 'Sync'}
                              </Button>
                            )}
                            <Button 
                              variant="outlined" 
                              size="small"
                              color="error"
                              onClick={() => integration.status === 'connected' ? handleDisconnect(integration._id) : handleDelete(integration._id)}
                            >
                              {integration.status === 'connected' ? 'Disconnect' : 'Delete'}
                            </Button>
                          </div>
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper style={{ padding: "16px" }}>
              <Typography variant="subtitle1" gutterBottom>
                Available Integrations
              </Typography>
              
              <List>
                {availableIntegrations
                  .filter(avail => !integrations.some(conn => conn.name === avail.name))
                  .map((integration, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar style={{ backgroundColor: '#CBD5E0' }}>
                          {getCategoryIcon(integration.category)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={integration.name}
                        secondary={integration.description}
                      />
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => {
                          setSelectedIntegration(integration);
                          setConnectionData({ apiKey: '', apiSecret: '', accessToken: '', description: integration.description });
                          setConnectDialogOpen(true);
                        }}
                      >
                        Connect
                      </Button>
                    </ListItem>
                  ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Connect Dialog */}
        <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Connect {selectedIntegration?.name}</DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '16px' }}>
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: '16px' }}>
                {selectedIntegration?.description}
              </Typography>
              
              {selectedIntegration?.name === 'HubSpot' ? (
                <>
                  <Alert severity="info" style={{ marginBottom: '16px' }}>
                    HubSpot uses OAuth 2.0 for secure authentication. Click the button below to authorize access to your HubSpot account.
                  </Alert>
                  <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
                    You'll be redirected to HubSpot to log in and authorize the integration. After authorization, you'll be redirected back to this page.
                  </Typography>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="API Key (optional)"
                    value={connectionData.apiKey}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, apiKey: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                  />
                  
                  <TextField
                    fullWidth
                    label="API Secret (optional)"
                    value={connectionData.apiSecret}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, apiSecret: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                  />
                  
                  <TextField
                    fullWidth
                    label="Access Token (optional)"
                    value={connectionData.accessToken}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, accessToken: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                  />
                  
                  <TextField
                    fullWidth
                    label="Description"
                    value={connectionData.description}
                    onChange={(e) => setConnectionData(prev => ({ ...prev, description: e.target.value }))}
                    multiline
                    rows={2}
                  />
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setConnectDialogOpen(false);
              setSelectedIntegration(null);
              setConnectionData({ apiKey: '', apiSecret: '', accessToken: '', description: '' });
            }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleConnect} 
              disabled={connecting || !selectedIntegration}
              style={{
                backgroundColor: selectedIntegration?.name === 'HubSpot' ? '#FF7A59' : '#000000',
                color: 'white'
              }}
            >
              {connecting ? 'Connecting...' : selectedIntegration?.name === 'HubSpot' ? 'Authorize with HubSpot' : 'Connect'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Credentials Dialog */}
        <Dialog open={updateCredentialsDialogOpen} onClose={() => {
          setUpdateCredentialsDialogOpen(false);
          setSelectedIntegrationForUpdate(null);
          setUpdateCredentialsData({ accessToken: '', apiKey: '', apiSecret: '' });
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Update {selectedIntegrationForUpdate?.name} Credentials</DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '16px' }}>
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: '16px' }}>
                Enter your updated credentials. Leave fields empty if you don't want to change them.
              </Typography>
              
              {selectedIntegrationForUpdate?.name === 'HubSpot' && (
                <>
                  <TextField
                    fullWidth
                    label="Private App Access Token"
                    value={updateCredentialsData.apiKey}
                    onChange={(e) => setUpdateCredentialsData(prev => ({ ...prev, apiKey: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                    helperText="HubSpot Private App Access Token (starts with 'pat-')"
                    placeholder="Enter your Private App Access Token"
                  />
                  <Alert severity="info" style={{ marginBottom: "16px" }}>
                    Get your Private App Access Token from HubSpot → Settings → Integrations → Private Apps
                  </Alert>
                </>
              )}
              {(selectedIntegrationForUpdate?.name === 'Buffer' || selectedIntegrationForUpdate?.name === 'Mailchimp') && (
                <>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={updateCredentialsData.apiKey}
                    onChange={(e) => setUpdateCredentialsData(prev => ({ ...prev, apiKey: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                    placeholder="Enter new API key"
                  />
                  <TextField
                    fullWidth
                    label="API Secret"
                    value={updateCredentialsData.apiSecret}
                    onChange={(e) => setUpdateCredentialsData(prev => ({ ...prev, apiSecret: e.target.value }))}
                    style={{ marginBottom: "16px" }}
                    type="password"
                    placeholder="Enter new API secret"
                  />
                </>
              )}
              
              <Alert severity="info" style={{ marginTop: '16px' }}>
                After updating, the credentials will be validated automatically. If validation fails, you'll see an error message.
              </Alert>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUpdateCredentialsDialogOpen(false);
              setSelectedIntegrationForUpdate(null);
              setUpdateCredentialsData({ accessToken: '', apiKey: '', apiSecret: '' });
            }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleUpdateCredentials} 
              disabled={updatingCredentials || (!updateCredentialsData.accessToken && !updateCredentialsData.apiKey && !updateCredentialsData.apiSecret)}
            >
              {updatingCredentials ? 'Updating...' : 'Update Credentials'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Main Advanced Features Component
export const AdvancedFeatures: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const features = [
    { id: 'copilot', label: 'AI Copilot', icon: <SmartToyIcon /> },
    { id: 'success', label: 'Success Rates', icon: <TrendingUpIcon /> },
    { id: 'multimedia', label: 'Multimedia', icon: <ImageIcon /> },
    { id: 'collaboration', label: 'Collaboration', icon: <GroupIcon /> },
    { id: 'integrations', label: 'Integrations', icon: <LinkIcon /> }
  ];

  // Get active feature from URL or default to 'copilot'
  const activeFeature = tab || 'copilot';

  // Redirect to default tab if no tab in URL and we're at /advanced
  useEffect(() => {
    if (location.pathname === '/advanced' && !tab) {
      navigate('/advanced/copilot', { replace: true });
    }
  }, [location.pathname, tab, navigate]);

  const handleTabChange = (featureId: string) => {
    navigate(`/advanced/${featureId}`);
  };

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(15px)',
      borderRadius: '16px',
      padding: '24px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    }}>
      <Typography variant="h4" gutterBottom style={{ color: '#000000', fontWeight: 'bold' }}>
        Advanced Features
      </Typography>
      
      {/* Feature Navigation */}
      <div style={{ marginBottom: "24px" }}>
        {features.map((feature) => (
          <Button
            key={feature.id}
            variant={activeFeature === feature.id ? 'contained' : 'outlined'}
            startIcon={feature.icon}
            onClick={() => handleTabChange(feature.id)}
            style={{ marginRight: '16px', marginBottom: '8px' }}
          >
            {feature.label}
          </Button>
        ))}
      </div>

      {/* Feature Content */}
      {activeFeature === 'copilot' && <AICopilot />}
      {activeFeature === 'success' && <HistoricalSuccessRates />}
      {activeFeature === 'multimedia' && <MultimediaManagement />}
      {activeFeature === 'collaboration' && <CollaborationTools />}
      {activeFeature === 'integrations' && <IntegrationEcosystem />}
                </div>
  );
};
